import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Spin, Table, Button, Row, Col, Checkbox } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import cx from 'classnames';
import moment from 'moment';
import queryString from 'query-string';
import { useDidUpdate } from 'rooks';
import { isNil } from 'lodash';

import { ExtraItemEnum } from 'enums';
import {
  getTimesheetStatusReportDetailExcel,
  getTimesheetStatusReportDetails,
  TimesheetStatusReportDetailsRequestModel,
  TimesheetStatusReportExcelRequestModel,
} from 'redux/reducers/homeDashboardSlice';
import {
  TimesheetStatusReportDetailResponseModel,
  TimesheetStatusReportDetailModel,
  TimesheetStatusEnum,
} from 'api/backofficeApiClient';
import {
  isAdminSelector,
  isCorporateTASelector,
} from 'redux/selectors/authenticationSelectors';
import {
  typeConversionToNumberArray,
  typeConversionToStringArray,
} from 'utils/convertUtils';
import SendReportModal from './SendReportModal';
import { getFullName } from 'utils/personUtils';
import {
  getDateWithoutTime,
  getDateRangeTillLastSunday,
} from 'utils/dateUtils';
import { downloadFileByObjectURL } from 'utils/fileUtils';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import BasePageLabel from 'components/wrappers/BasePageLabel';
import { TimesheetStatusReportFilterTypes } from './filters/enums';
import TimesheetStatusReportFilter from './TimesheetStatusReportFilter';
import { getTimesheetStatusReportTableColumns } from 'utils/dashboardUtils';
import { TimesheetStatusReportFilters } from './TimesheetStatusReportFilter';
import LastSyncDateSection from 'components/LastSyncDateSection';
import BaseTableHeader from 'components/wrappers/BaseTableHeader';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

import styles from './TimesheetStatusReportDetailsPage.module.less';
import commonStyles from '../UserHomePage.module.less';

const columns: ColumnsType<TimesheetStatusReportDetailModel> = [
  {
    title: 'Member',
    key: 'member',
    render: (record: TimesheetStatusReportDetailModel) => {
      return {
        props: {
          'data-id': `member-${record.memberKey}`,
        },
        children: (
          <>
            {record.companyName && (
              <Link to={`/member/${record.memberKey}`}>
                {`${record.companyName}`}
              </Link>
            )}
            {record.memberKey && <div>{record.memberKey}</div>}
          </>
        ),
      };
    },
  },
  {
    title: 'Worker',
    key: 'workerName',
    render: (record: TimesheetStatusReportDetailModel) => {
      const workerFullName = getFullName(
        record.workerFirstName,
        record.workerLastName,
      );
      return {
        props: {
          'data-id': `worker-${record.workerKey}`,
        },
        children: (
          <>
            {workerFullName && (
              <Link className="wrapped-text" to={`/worker/${record.workerKey}`}>
                {workerFullName}
              </Link>
            )}
            {record.workerKey && <div>{record.workerKey}</div>}
          </>
        ),
      };
    },
  },
  {
    title: 'Customer',
    key: 'customer',
    render: (record: TimesheetStatusReportDetailModel) => {
      return {
        props: {
          'data-id': `customerKey-${record.customerKey}`,
        },
        children: (
          <>
            {record.customerName && (
              <Link to={`/customer/${record.customerKey}`}>
                {record.customerName}
              </Link>
            )}
            {record.customerKey && <div>{record.customerKey}</div>}
          </>
        ),
      };
    },
  },
  {
    title: 'Assignment ID',
    key: 'assignmentId',
    render: (record: TimesheetStatusReportDetailModel) => {
      return {
        props: {
          'data-id': `assignment-${record.assignmentKey}`,
        },
        children: (
          <>
            {record.assignmentKey && (
              <Link to={`/assignment/${record.assignmentKey}`}>
                {record.assignmentKey}
              </Link>
            )}
          </>
        ),
      };
    },
  },
  {
    title: 'Hours',
    key: 'hours',
    render: (record: TimesheetStatusReportDetailModel) => {
      return {
        props: { 'data-id': `hours-${record.hours}` },
        children: record?.hours || '',
      };
    },
  },
  {
    title: 'PTO hours',
    key: 'hours',
    render: (record: TimesheetStatusReportDetailModel) => {
      return {
        props: { 'data-id': `pto-${record.pto}` },
        children: record?.pto || '',
      };
    },
  },
  {
    title: 'State',
    key: 'state',
    render: (record: TimesheetStatusReportDetailModel) => {
      return {
        props: { 'data-id': `pto-${record.state}` },
        children: record?.state || '',
      };
    },
  },
  {
    title: 'Timesheet start',
    key: 'timesheetStartDate',
    render: (record: TimesheetStatusReportDetailModel) => {
      return {
        props: {
          'data-id': `timesheetStartDate-${record.timesheetStartDate}`,
        },
        children: record?.timesheetStartDate
          ? moment.utc(record.timesheetStartDate).format('MM/DD/YYYY')
          : '',
      };
    },
  },
  {
    title: 'Timesheet end',
    key: 'timesheetEndDate',
    render: (record: TimesheetStatusReportDetailModel) => {
      return {
        props: {
          'data-id': `timesheetEndDate-${record.timesheetEndDate}`,
        },
        children: record?.timesheetEndDate
          ? moment.utc(record.timesheetEndDate).format('MM/DD/YYYY')
          : '',
      };
    },
  },
  {
    title: 'Approved on',
    key: 'approvedOn',
    render: (record: TimesheetStatusReportDetailModel) => {
      return {
        props: {
          'data-id': `approvedOn-${record.approvedOn}`,
        },
        children: record?.approvedOn
          ? moment.utc(record.approvedOn).format('MM/DD/YYYY')
          : '',
      };
    },
  },
  {
    title: 'Approver',
    key: 'approver',
    render: (record: TimesheetStatusReportDetailModel) => {
      return {
        props: {
          'data-id': `approver-${record.approver}`,
        },
        children: record?.approver || '',
      };
    },
  },
  {
    title: 'Assignment type',
    key: 'assignmentType',
    render: (record: TimesheetStatusReportDetailModel) => {
      return {
        props: {
          'data-id': `assignmentType-${record.assignmentType}`,
        },
        children: record?.assignmentType || '',
      };
    },
  },
  {
    title: 'Timesheet type',
    key: 'timesheetType',
    render: (record: TimesheetStatusReportDetailModel) => {
      return {
        props: {
          'data-id': `timesheetType-${record.timesheetType}`,
        },
        children: record?.timesheetType || '',
      };
    },
  },
];

const pageSize = 12;

enum CurrentPeriodCheckboxState {
  UnChecked,
  Checked,
}

const getInitialDateFilter = (isCurrentPeriod: boolean) =>
  isCurrentPeriod ? getDateRangeTillLastSunday(6, 'd') : undefined;

export default function TimesheetStatusReportDetailsPage() {
  const dispatch = useAppDispatch();
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [
    isTimesheetStatusReportDataLoading,
    setIsTimesheetStatusReportDataLoading,
  ] = useState(false);
  const [timesheetStatusReportData, setTimesheetStatusReportData] =
    useState<TimesheetStatusReportDetailResponseModel>();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    approvalState,
    customer,
    member,
    worker,
    startDate,
    endDate,
    onlyCurrent,
  } = queryString.parse(location.search);
  const isCurrentPeriodEnabled =
    // Enabled by default
    isNil(onlyCurrent) || +onlyCurrent === CurrentPeriodCheckboxState.Checked;
  const [isDisplayCurrentPeriod, setIsDisplayCurrentPeriod] = useState(
    isCurrentPeriodEnabled,
  );
  const initialDateFilter = getInitialDateFilter(isCurrentPeriodEnabled);
  const initialFilters: TimesheetStatusReportFilters = {
    [TimesheetStatusReportFilterTypes.Date]:
      startDate && endDate
        ? { startDate: moment(startDate), endDate: moment(endDate) }
        : initialDateFilter,
    [TimesheetStatusReportFilterTypes.Worker]:
      typeConversionToStringArray(worker),
    [TimesheetStatusReportFilterTypes.Customer]:
      typeConversionToStringArray(customer),
    [TimesheetStatusReportFilterTypes.Member]:
      typeConversionToStringArray(member),
    [TimesheetStatusReportFilterTypes.ApprovalState]:
      typeConversionToNumberArray(approvalState),
  };
  const [filters, setFilters] =
    useState<TimesheetStatusReportFilters>(initialFilters);
  const [currentTablePage, setCurrentTablePage] = useState(1);
  const isCorporateTA = useAppSelector(isCorporateTASelector);
  const isAdmin = useAppSelector(isAdminSelector);
  const [isSendReportModalOpen, setIsSendReportModalOpen] = useState(false);
  const tableColumns = getTimesheetStatusReportTableColumns({
    columns,
    isAdmin,
    isCorporateTA,
  });

  useDidUpdate(() => {
    const queryData = {
      onlyCurrent: isDisplayCurrentPeriod
        ? CurrentPeriodCheckboxState.Checked
        : CurrentPeriodCheckboxState.UnChecked,
      startDate: filters[TimesheetStatusReportFilterTypes.Date]?.startDate
        ? filters[TimesheetStatusReportFilterTypes.Date]?.startDate?.format(
            'MM/DD/YYYY',
          )
        : undefined,
      endDate: filters[TimesheetStatusReportFilterTypes.Date]?.endDate
        ? filters[TimesheetStatusReportFilterTypes.Date]?.endDate?.format(
            'MM/DD/YYYY',
          )
        : undefined,
      [TimesheetStatusReportFilterTypes.Worker]:
        filters[TimesheetStatusReportFilterTypes.Worker],
      [TimesheetStatusReportFilterTypes.Customer]:
        filters[TimesheetStatusReportFilterTypes.Customer],
      [TimesheetStatusReportFilterTypes.Member]:
        filters[TimesheetStatusReportFilterTypes.Member],
      [TimesheetStatusReportFilterTypes.ApprovalState]:
        filters[TimesheetStatusReportFilterTypes.ApprovalState],
    };

    navigate(`${location.pathname}?${queryString.stringify(queryData)}`, {
      replace: true,
    });
    // eslint-disable-next-line
  }, [filters, isDisplayCurrentPeriod]);

  const loadTimesheetStatusReportDetails = useCallback(async () => {
    setIsTimesheetStatusReportDataLoading(true);

    const detailsRequestOptions: TimesheetStatusReportDetailsRequestModel = {
      isAdmin,
      pageNumber: currentTablePage - 1,
      pageSize,
      state: !filters[TimesheetStatusReportFilterTypes.ApprovalState].includes(
        TimesheetStatusEnum.All,
      )
        ? filters[TimesheetStatusReportFilterTypes.ApprovalState]
        : [],
      customerIds: !filters[TimesheetStatusReportFilterTypes.Customer].includes(
        ExtraItemEnum.All,
      )
        ? filters[TimesheetStatusReportFilterTypes.Customer]
        : [],
      workerIds: !filters[TimesheetStatusReportFilterTypes.Worker].includes(
        ExtraItemEnum.All,
      )
        ? filters[TimesheetStatusReportFilterTypes.Worker]
        : [],
      startDate: filters[TimesheetStatusReportFilterTypes.Date]?.startDate
        ? getDateWithoutTime(
            filters[TimesheetStatusReportFilterTypes.Date]?.startDate,
          )
        : undefined,
      endDate: filters[TimesheetStatusReportFilterTypes.Date]?.endDate
        ? getDateWithoutTime(
            filters[TimesheetStatusReportFilterTypes.Date]?.endDate,
          )
        : undefined,
    };

    if (isAdmin) {
      detailsRequestOptions.companyIds = !filters[
        TimesheetStatusReportFilterTypes.Member
      ].includes(ExtraItemEnum.All)
        ? filters[TimesheetStatusReportFilterTypes.Member]
        : [];
    }

    const result = await dispatch(
      getTimesheetStatusReportDetails(detailsRequestOptions),
    );

    if (getTimesheetStatusReportDetails.fulfilled.match(result)) {
      setTimesheetStatusReportData(result.payload);
    } else {
      setTimesheetStatusReportData({
        totalCount: 0,
        details: [],
      });
    }

    setIsTimesheetStatusReportDataLoading(false);
  }, [dispatch, filters, currentTablePage, isAdmin]);

  const onFilterChange = (
    filters: TimesheetStatusReportFilters,
    type?: TimesheetStatusReportFilterTypes,
  ) => {
    setFilters(filters);
    setCurrentTablePage(1);

    if (type === TimesheetStatusReportFilterTypes.Date) {
      setIsDisplayCurrentPeriod(false);
    }
  };

  const loadInitialData = useCallback(async () => {
    await loadTimesheetStatusReportDetails();

    setIsInitialDataLoaded(true);
  }, [loadTimesheetStatusReportDetails]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const handleDownloadReport = async () => {
    const excelRequestOptions: TimesheetStatusReportExcelRequestModel = {
      isAdmin,
      pageNumber: currentTablePage,
      pageSize,
      states: !filters[TimesheetStatusReportFilterTypes.ApprovalState].includes(
        TimesheetStatusEnum.All,
      )
        ? filters[TimesheetStatusReportFilterTypes.ApprovalState]
        : [],
      customerIds: !filters[TimesheetStatusReportFilterTypes.Customer].includes(
        ExtraItemEnum.All,
      )
        ? filters[TimesheetStatusReportFilterTypes.Customer]
        : [],
      workerIds: !filters[TimesheetStatusReportFilterTypes.Worker].includes(
        ExtraItemEnum.All,
      )
        ? filters[TimesheetStatusReportFilterTypes.Worker]
        : [],
      startDate: filters[TimesheetStatusReportFilterTypes.Date]
        ? getDateWithoutTime(
            filters[TimesheetStatusReportFilterTypes.Date]?.startDate,
          )
        : undefined,
      endDate: filters[TimesheetStatusReportFilterTypes.Date]
        ? getDateWithoutTime(
            filters[TimesheetStatusReportFilterTypes.Date]?.endDate,
          )
        : undefined,
    };

    if (isAdmin) {
      excelRequestOptions.companyIds = !filters[
        TimesheetStatusReportFilterTypes.Member
      ].includes(ExtraItemEnum.All)
        ? filters[TimesheetStatusReportFilterTypes.Member]
        : [];
    }

    const result = await dispatch(
      getTimesheetStatusReportDetailExcel(excelRequestOptions),
    );

    if (getTimesheetStatusReportDetailExcel.fulfilled.match(result)) {
      downloadFileByObjectURL(result.payload.url, result.payload.fileName);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleOnlyCurrentPeriodChange = (checked: boolean) => {
    setCurrentTablePage(1);
    setIsDisplayCurrentPeriod(checked);

    const dateFilter = getInitialDateFilter(checked);
    setFilters((filters) => ({
      ...filters,
      [TimesheetStatusReportFilterTypes.Date]: dateFilter,
    }));
  };

  return (
    <div className="page-wrapper scroll-box">
      {isSendReportModalOpen && (
        <SendReportModal
          onClose={() => setIsSendReportModalOpen(false)}
          filters={filters}
          isAdmin={isAdmin}
          pageNumber={currentTablePage}
          pageSize={pageSize}
        />
      )}

      <div className="page-holder">
        <BasePageLabel label="Dashboard details">
          {timesheetStatusReportData?.lastKronosSyncDate && (
            <div className="title-line__info">
              <LastSyncDateSection
                syncDate={timesheetStatusReportData.lastKronosSyncDate}
                title="Timesheet data is automatically refreshed every hour."
              />
            </div>
          )}
        </BasePageLabel>

        <div className="page-box page-inner">
          <div className="page-inner-container">
            <BaseTableHeader
              title="Timesheet status report"
              classes={{
                wrapper: 'align-start',
                actions: {
                  [styles.userFilters]: !isAdmin,
                  [styles.adminFilters]: isAdmin,
                },
              }}
            >
              <TimesheetStatusReportFilter
                filters={filters}
                isAdmin={isAdmin}
                onChange={onFilterChange}
                isCorporateTA={isCorporateTA}
              />
              <div className={styles.checkBox}>
                <Checkbox
                  data-id="isDisplayCurrentPeriod"
                  checked={isDisplayCurrentPeriod}
                  onChange={(e) =>
                    handleOnlyCurrentPeriodChange(e.target.checked)
                  }
                >
                  Display only current period
                </Checkbox>
              </div>

              <div>
                <Button
                  className={commonStyles.saveBtn}
                  onClick={handleDownloadReport}
                  icon={<span className="icon icon-xls-file" />}
                />
              </div>

              <div>
                <Button
                  className={commonStyles.saveBtn}
                  onClick={() => setIsSendReportModalOpen(true)}
                  icon={<span className="icon icon-send" />}
                />
              </div>
            </BaseTableHeader>
          </div>

          {!isInitialDataLoaded && <Spin />}
          {isInitialDataLoaded && (
            <Row gutter={[10, 10]}>
              <Col xs={24}>
                <Table
                  className={cx('custom-table', styles.table)}
                  pagination={{
                    total: timesheetStatusReportData?.totalCount,
                    pageSize,
                    onChange: setCurrentTablePage,
                    current: currentTablePage,
                    showSizeChanger: false,
                  }}
                  dataSource={timesheetStatusReportData?.details}
                  columns={tableColumns}
                  rowKey={(record) => `${record.id}`}
                  loading={isTimesheetStatusReportDataLoading}
                  scroll={{ x: '100%' }}
                />
              </Col>
            </Row>
          )}
        </div>
      </div>
    </div>
  );
}
